import { ReactElement, useState } from 'react';
import { HiOutlineChevronRight, HiOutlineDevicePhoneMobile } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { cn } from '../../../utils/cn';
import { Episode } from '../../../api/episodes';
import { useSessionEpisodes, useSetEpisodeDbUpload, useSetEpisodeDbUploadBatch } from '../../../hooks/useEpisodes';

/**
 * "Nimbus Recordings" card for the session-details page: one row per
 * episode bound to this session, with the on-demand full-.db upload
 * request control. Requesting flags the episode server-side; the phone
 * checks at next app start and uploads (prompting the user first when
 * the file is large). Silent when the session has no episodes.
 */

const STATE_CHIPS: Record<string, { label: string; className: string }> = {
  requested: { label: 'upload requested', className: 'cr-bg-amber-100 cr-text-amber-800' },
  fulfilled: { label: 'uploaded', className: 'cr-bg-green-100 cr-text-green-800' },
  denied: { label: 'declined on phone', className: 'cr-bg-red-100 cr-text-red-700' },
};

function whenLabel(ep: Episode): string {
  const ts = ep.db_upload_state === 'requested' ? ep.db_upload_requested_at : ep.db_upload_resolved_at;
  return ts ? new Date(ts).toLocaleString() : '';
}

function EpisodeRow({ ep, sessionId }: { ep: Episode; sessionId?: string }): ReactElement {
  const setUpload = useSetEpisodeDbUpload(sessionId);
  const state = ep.db_upload_state ?? null;
  const chip = state ? STATE_CHIPS[state] : null;
  const requesting = setUpload.isPending;

  const change = (next: 'requested' | null) => {
    setUpload.mutate(
      { episodeId: ep.id, state: next },
      {
        onSuccess: () => toast.success(next === 'requested' ? 'Upload requested' : 'Request cancelled'),
        onError: (err) => toast.error(`Failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-border-t cr-border-gray-100 cr-py-2">
      <span
        className="cr-flex cr-items-center cr-gap-1 cr-text-sm cr-font-medium cr-text-gray-700"
        title={`Device ${ep.device_id || 'unknown'} — episode ${ep.id}`}
      >
        <HiOutlineDevicePhoneMobile className="cr-h-4 cr-w-4" />
        {ep.device_id ? ep.device_id.slice(-4) : '????'}
      </span>
      <span className="cr-text-xs cr-text-gray-500">
        {new Date(ep.start_ts_ms).toLocaleString()}
      </span>
      <span className="cr-text-xs cr-text-gray-400">({ep.live_state})</span>
      {chip && (
        <span
          className={cn('cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-semibold', chip.className)}
          title={whenLabel(ep)}
        >
          {chip.label}
        </span>
      )}
      <span className="cr-grow" />
      {state === 'requested' ? (
        <button
          onClick={() => change(null)}
          disabled={requesting}
          className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
            cr-font-medium cr-text-gray-700 hover:cr-bg-gray-50 disabled:cr-opacity-50"
        >
          Cancel request
        </button>
      ) : (
        <button
          onClick={() => change('requested')}
          disabled={requesting || !ep.device_id}
          title={ep.device_id
            ? 'Ask the phone to upload its full recording file at next app start'
            : 'No device id recorded for this episode'}
          className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
            cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
        >
          {state === 'fulfilled' ? 'Request again' : 'Request recording'}
        </button>
      )}
    </div>
  );
}

export default function NimbusRecordingsCard({ sessionId }: { sessionId?: string }): ReactElement | null {
  const [expanded, setExpanded] = useState(false);
  const { data: episodes } = useSessionEpisodes(sessionId);
  const batchUpload = useSetEpisodeDbUploadBatch(sessionId);

  if (!episodes?.length) return null;
  const openRequests = episodes.filter((ep) => ep.db_upload_state === 'requested').length;
  const requestable = episodes.filter((ep) => ep.device_id && !ep.db_upload_state);

  const requestAll = () => {
    const asked = requestable.length;
    batchUpload.mutate(
      { episodeIds: requestable.map((ep) => ep.id), state: 'requested' },
      {
        // Count the episodes the server says it wrote, not the ones we sent:
        // the batch PATCH skips a body it cannot apply and still answers 200,
        // so onError never fires for the common partial failure.
        onSuccess: (updated) => {
          const done = updated.length;
          if (done < asked) {
            toast.error(`Requested ${done} of ${asked} recordings — ${asked - done} could not be flagged`);
          } else {
            toast.success(`Requested ${done} recording${done === 1 ? '' : 's'}`);
          }
        },
        onError: (err) => toast.error(`Failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="cr-mt-2 cr-w-full cr-rounded-lg cr-bg-white cr-px-3 cr-py-2 cr-shadow-md cr-font-main">
      <button
        className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-text-left cr-text-sm cr-text-gray-700"
        onClick={() => setExpanded((v) => !v)}
      >
        <HiOutlineChevronRight
          className={cn('cr-h-4 cr-w-4 cr-transition-transform cr-duration-200', expanded && 'cr-rotate-90')}
        />
        <span className="cr-font-semibold">Nimbus Recordings</span>
        <span className="cr-text-xs cr-text-gray-500">
          {episodes.length} episode{episodes.length === 1 ? '' : 's'}
          {openRequests > 0 && ` · ${openRequests} upload request${openRequests === 1 ? '' : 's'} open`}
        </span>
      </button>
      {expanded && (
        <div className="cr-mt-2">
          {requestable.length > 1 && (
            <div className="cr-flex cr-justify-end cr-pb-1">
              <button
                onClick={requestAll}
                disabled={batchUpload.isPending}
                className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
                  cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
              >
                Request all ({requestable.length})
              </button>
            </div>
          )}
          {episodes.map((ep) => (
            <EpisodeRow key={ep.id} ep={ep} sessionId={sessionId} />
          ))}
        </div>
      )}
    </div>
  );
}
